import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import {
	json,
	redirect,
	type MetaFunction,
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
} from '@remix-run/node'
import { Form, useActionData } from '@remix-run/react'
import { type TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { getLinkByRoute } from '#app/components/locale-link.tsx'
import { Logo } from '#app/components/logo.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { MenuContainer } from '#app/features/menu/container.tsx'
import {
	getUserId,
	requireAnonymous,
	resetUserPassword,
} from '#app/utils/auth.server.ts'

import { prisma } from '#app/utils/db.server.ts'
import { invariant, useIsPending } from '#app/utils/misc.tsx'
import { mixpanel } from '#app/utils/mixpanel.server.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { PasswordAndConfirmPasswordSchema } from '#app/utils/user-validation.ts'
import { verifySessionStorage } from '#app/utils/verification.server.ts'
import { getLanguageIdFromPathname } from '#server/utils/getLanguageIdFromPathname.ts'
import { serverTranslations } from '#server/utils/serverTranslations.ts'
import { type VerifyFunctionArgs } from './verify.tsx'

const resetPasswordUsernameSessionKey = 'resetPasswordUsername'

export async function handleVerification({
	submission,
	request,
}: VerifyFunctionArgs) {
	invariant(submission.value, 'submission.value should be defined by now')
	const currentLanguageId = getLanguageIdFromPathname(request)
	const target = submission.value.target
	const user = await prisma.user.findFirst({
		where: { OR: [{ email: target }, { username: target }] },
		select: { email: true, username: true },
	})
	// we don't want to say the user is not found if the email is not found
	// because that would allow an attacker to check if an email is registered
	if (!user) {
		submission.error.code = ['Invalid code']
		return json({ status: 'error', submission } as const, { status: 400 })
	}

	const url = new URL(request.url)
	const redirectTo = url.searchParams.get('redirectTo')

	let redirectUrl = `/${currentLanguageId}/reset-password`

	if (redirectTo) {
		redirectUrl += `?redirectTo=${encodeURIComponent(redirectTo)}`
	}

	const verifySession = await verifySessionStorage.getSession()
	verifySession.set(resetPasswordUsernameSessionKey, user.username)
	return redirect(redirectUrl, {
		headers: {
			'set-cookie': await verifySessionStorage.commitSession(verifySession),
		},
	})
}

async function requireResetPasswordUsername(request: Request) {
	await requireAnonymous(request)
	const verifySession = await verifySessionStorage.getSession(
		request.headers.get('cookie'),
	)
	const resetPasswordUsername = verifySession.get(
		resetPasswordUsernameSessionKey,
	)
	if (typeof resetPasswordUsername !== 'string' || !resetPasswordUsername) {
		throw redirect('/login')
	}
	return resetPasswordUsername
}

export async function loader({ request }: LoaderFunctionArgs) {
	const resetPasswordUsername = await requireResetPasswordUsername(request)

	const languageId = getLanguageIdFromPathname(request)
	const userId = await getUserId(request)

	mixpanel.track(`PageView: ${languageId} - Password Reset Landingpage`, {
		distinct_id: userId ?? 'anonymous',
		timeStamp: new Date().toISOString(),
	})

	return json({ resetPasswordUsername })
}

const formErrorsLoader = (t: TFunction) => {
	return {
		password: {
			required: t('passwordSchema.required'),
			minLength: t('passwordSchema.minLength'),
			maxLength: t('passwordSchema.maxLength'),
		},
		confirmPassword: {
			matches: t('passwordConfirmSchema.matches'),
		},
	}
}

export async function action({ request }: ActionFunctionArgs) {
	const resetPasswordUsername = await requireResetPasswordUsername(request)
	const formData = await request.formData()
	const currentLanguageId = getLanguageIdFromPathname(request)
	const t = await serverTranslations(request, ['form', 'auth'])
	const formErrors = formErrorsLoader(t)
	const submission = parse(formData, {
		schema: PasswordAndConfirmPasswordSchema(
			formErrors.password,
			formErrors.confirmPassword,
		),
	})
	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value?.password) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}
	const { password } = submission.value

	await resetUserPassword({ username: resetPasswordUsername, password })
	const verifySession = await verifySessionStorage.getSession()

	return redirectWithToast(
		safeRedirect(
			getLinkByRoute({ linkRouteId: 'login', locale: currentLanguageId }),
		),
		{
			title: t('resetPassword.confirmationTitle', { ns: 'auth' }),
			description: t('resetPassword.confirmationSubtitle', { ns: 'auth' }),
		},
		{
			headers: {
				'set-cookie': await verifySessionStorage.destroySession(verifySession),
			},
		},
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Reset Password - Wiser' }]
}

export default function ResetPasswordPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const { t } = useTranslation(['form', 'auth'])
	const formErrors = formErrorsLoader(t)
	const [form, fields] = useForm({
		id: 'reset-password',
		constraint: getFieldsetConstraint(
			PasswordAndConfirmPasswordSchema(
				formErrors.password,
				formErrors.confirmPassword,
			),
		),
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			return parse(formData, {
				schema: PasswordAndConfirmPasswordSchema(
					formErrors.password,
					formErrors.confirmPassword,
				),
			})
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<MenuContainer showLanguageForm showFooter={false} showLogo={false}>
			<div className="flex h-full flex-col items-center justify-center px-4 ">
				<div className="mx-auto mt-16 min-w-full max-w-sm sm:min-w-[368px]">
					<Form method="POST" {...form.props}>
						<div className="flex flex-col  text-left">
							<Logo />
							<p className="mb-5 text-body-lg-bold text-primary-500">
								{t('resetPassword.title', { ns: 'auth' })}
							</p>
						</div>
						<Field
							labelProps={{
								htmlFor: fields.password.id,
							}}
							inputProps={{
								...conform.input(fields.password, { type: 'password' }),
								autoComplete: 'new-password',
								autoFocus: true,
								placeholder: t('resetPassword.newPassword', { ns: 'auth' }),
							}}
							errors={fields.password.errors}
						/>
						<Field
							labelProps={{
								htmlFor: fields.confirmPassword.id,
							}}
							inputProps={{
								...conform.input(fields.confirmPassword, { type: 'password' }),
								autoComplete: 'new-password',
								placeholder: t('resetPassword.confirmPassword', { ns: 'auth' }),
							}}
							errors={fields.confirmPassword.errors}
						/>

						<ErrorList errors={form.errors} id={form.errorId} />

						<StatusButton
							className="w-full"
							status={isPending ? 'pending' : actionData?.status ?? 'idle'}
							type="submit"
							disabled={isPending}
						>
							{t('resetPassword.title', { ns: 'auth' })}
						</StatusButton>
					</Form>
				</div>
			</div>
		</MenuContainer>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
